import { gettext } from '@eventbrite/i18n';
import {
    ArrowLeftIcon,
    ArrowRightIcon,
    Typography,
} from '@eventbrite/marmalade';
import { useDatePicker } from '@rehookify/datepicker';
import clsx from 'classnames';
import React, { useRef } from 'react';
import styles from './Datepicker.module.scss';

export enum DatepickerTestIds {
    Container = 'datepickerContainer',
    Calendar = 'calendarContainer',
    WeekDay = 'calendarWeekDay',
    Day = 'calendarDay',
}

type Style = keyof typeof styles;

export interface DatepickerProps {
    onDatesChange: (d: Date[]) => void;
    selectedDates: Date[];
    startDay?: 0 | 1;
    minDate?: Date;
    maxDate?: Date;
    locale?: string;
    mode: 'single' | 'multiple' | 'range';
}

export const Datepicker = (props: DatepickerProps) => {
    const {
        onDatesChange,
        selectedDates,
        minDate = new Date(),
        maxDate,
        startDay = 1,
        locale = 'en-US',
        mode,
    } = props;

    const {
        data: { calendars, weekDays },
        propGetters: { dayButton, addOffset, subtractOffset },
    } = useDatePicker({
        selectedDates,
        onDatesChange,
        dates: {
            mode,
            selectSameDate: true,
            minDate,
            maxDate,
        },
        calendar: {
            offsets: [0],
            startDay,
        },
        locale: {
            locale,
            day: 'numeric',
            weekday: 'short',
        },
    });

    const calendarsRef = useRef<HTMLDivElement>(null);
    const { days, month, year } = calendars[0];
    const monthNumber = monthNumberFromString(month, year);
    const ref = useRef<HTMLDivElement>(null);

    return (
        <div
            data-testid={DatepickerTestIds.Container}
            className={styles.container}
            ref={calendarsRef}
        >
            <nav className={styles.nav}>
                <div className={styles.action}>
                    <button
                        {...subtractOffset({ months: 1 })}
                        type="button"
                        aria-label={gettext('Next month').toString()}
                    >
                        <ArrowLeftIcon size="xsmall" />
                    </button>
                </div>
                <div className={styles.action}>
                    <button
                        {...addOffset({ months: 1 })}
                        type="button"
                        aria-label={gettext('Previous month').toString()}
                    >
                        <ArrowRightIcon size="xsmall" />
                    </button>
                </div>
            </nav>

            <section
                data-calendar={`${month}-${year}`}
                className={styles.calendar}
                data-testid={DatepickerTestIds.Calendar}
                ref={ref}
            >
                <header className={styles.month}>
                    <Typography as="p" variant="body-lg" color="neutral-900">
                        {month} {year}
                    </Typography>
                </header>

                <div className={styles.weekDays}>
                    {weekDays.map((d) => (
                        <Typography
                            data-testid={DatepickerTestIds.WeekDay}
                            as="p"
                            variant="body-md"
                            key={d}
                            color="neutral-800"
                        >
                            {d.substring(0, 2)}
                        </Typography>
                    ))}
                </div>

                <div className={styles.weeks}>
                    {days.map((d) => {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const { ['aria-selected']: _, ...props } = dayButton(d);
                        return (
                            <div key={d.$date.toString()}>
                                <button
                                    type="button"
                                    className={clsx(
                                        styles.day,
                                        styles[d.range as Style],
                                        {
                                            [styles.selected]: d.selected,
                                            [styles.today]: d.now,
                                            [styles['in-current-month']]:
                                                d.inCurrentMonth,
                                        },
                                    )}
                                    data-testid={DatepickerTestIds.Day}
                                    data-date={`${year}-${monthNumber}-${d.day}`}
                                    {...props}
                                    aria-pressed={d.selected}
                                    onMouseDown={onBlurTrap}
                                >
                                    <time
                                        dateTime={`${year}-${monthNumber}-${d.day}`}
                                    >
                                        <Typography as="span" variant="body-md">
                                            {d.day}
                                        </Typography>
                                    </time>
                                </button>
                            </div>
                        );
                    })}
                </div>
            </section>
        </div>
    );
};

const monthNumberFromString = (month: string, year: string) => {
    return new Date(`${month} 01 ${year}`).toLocaleDateString([], {
        month: `2-digit`,
    });
};

const onBlurTrap = (e: React.MouseEvent) => e.preventDefault();
